import {z} from 'zod';

const MicrositeThemePaletteSchema = z
  .object({
    primary: z.string().min(1),
    secondary: z.string().min(1),
    accent: z.string().min(1),
  })
  .strict();

const MicrositeThemeTextSchema = z
  .object({
    base: z.string().min(1),
    muted: z.string().min(1),
    onLight: z.string().min(1),
    mutedOnLight: z.string().min(1),
    onDark: z.string().min(1),
    mutedOnDark: z.string().min(1),
  })
  .strict();

const MicrositeThemeBackgroundsSchema = z
  .object({
    page: z.string().min(1),
    surface: z.string().min(1),
    surfaceAlt: z.string().min(1),
    light: z.string().min(1),
    dark: z.string().min(1),
  })
  .strict();

const MicrositeThemeGradientsSchema = z
  .object({
    hero: z.string().min(1),
    sectionLight: z.string().min(1),
    sectionDark: z.string().min(1),
    primaryCta: z.string().min(1),
  })
  .strict();

const MicrositeThemeSectionLightSchema = z
  .object({
    background: z.string().min(1),
    patternColor: z.string().min(1),
    cardBackground: z.string().min(1),
    cardBorder: z.string().min(1),
  })
  .strict();

const MicrositeThemeSectionDarkSchema = z
  .object({
    background: z.string().min(1),
    cardBackground: z.string().min(1),
    cardBorder: z.string().min(1),
  })
  .strict();

const MicrositeThemeSectionsSchema = z
  .object({
    light: MicrositeThemeSectionLightSchema,
    dark: MicrositeThemeSectionDarkSchema,
  })
  .strict();

const MicrositeThemeCtaPrimarySchema = z
  .object({
    background: z.string().min(1),
    text: z.string().min(1),
    glow: z.string().min(1),
    highlight: z.string().min(1),
    shadow: z.string().min(1),
  })
  .strict();

const MicrositeThemeCtaSecondarySchema = z
  .object({
    text: z.string().min(1),
    hoverText: z.string().min(1),
    underline: z.string().min(1),
    background: z.string().min(1),
    hoverBackground: z.string().min(1),
    shadow: z.string().min(1),
  })
  .strict();

const MicrositeThemeCtasSchema = z
  .object({
    primary: MicrositeThemeCtaPrimarySchema,
    secondary: MicrositeThemeCtaSecondarySchema,
  })
  .strict();

const MicrositeThemeCardsSchema = z
  .object({
    surface: z.string().min(1),
    border: z.string().min(1),
    darkSurface: z.string().min(1),
    darkBorder: z.string().min(1),
  })
  .strict();

export const MicrositeThemeSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    palette: MicrositeThemePaletteSchema,
    text: MicrositeThemeTextSchema,
    backgrounds: MicrositeThemeBackgroundsSchema,
    gradients: MicrositeThemeGradientsSchema,
    sections: MicrositeThemeSectionsSchema,
    ctas: MicrositeThemeCtasSchema,
    cards: MicrositeThemeCardsSchema,
  })
  .strict();

export type MicrositeTheme = z.infer<typeof MicrositeThemeSchema>;

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

const baseMicrositeTheme: MicrositeTheme = {
  id: `base`,
  name: `Microsite Base`,
  palette: {
    primary: `#951c37`,
    secondary: `#415a77`,
    accent: `#f97316`,
  },
  text: {
    base: `#0f172a`,
    muted: `#475569`,
    onLight: `#0f172a`,
    mutedOnLight: `rgba(55, 65, 81, 0.75)`,
    onDark: `#f8fafc`,
    mutedOnDark: `rgba(226, 232, 240, 0.8)`,
  },
  backgrounds: {
    page: `linear-gradient(180deg, rgba(6, 9, 21, 0.98), rgba(12, 19, 34, 0.95) 28%, rgba(248, 250, 252, 0.94) 55%, rgba(8, 12, 23, 0.95) 88%)`,
    surface: `#ffffff`,
    surfaceAlt: `#f0f4f8`,
    light: `#f4f1fa`,
    dark: `#060915`,
  },
  gradients: {
    hero: `linear-gradient(135deg, #415a77, #951c37)`,
    sectionLight: `linear-gradient(135deg, rgba(255, 248, 244, 0.95), rgba(229, 231, 235, 0.9))`,
    sectionDark: `linear-gradient(160deg, #0b1120, #111827 38%, #020617 82%)`,
    primaryCta: `linear-gradient(120deg, #6c4bff 0%, #ff3fa4 50%, #ffb347 100%)`,
  },
  sections: {
    light: {
      background: `rgba(255, 255, 255, 0.96)`,
      patternColor: `#f4f1fa`,
      cardBackground: `rgba(255, 255, 255, 0.9)`,
      cardBorder: `rgba(148, 163, 184, 0.18)`,
    },
    dark: {
      background: `rgba(5, 6, 13, 0.95)`,
      cardBackground: `rgba(255, 255, 255, 0.12)`,
      cardBorder: `rgba(255, 255, 255, 0.18)`,
    },
  },
  ctas: {
    primary: {
      background: `linear-gradient(120deg, #6c4bff 0%, #ff3fa4 50%, #ffb347 100%)`,
      text: `#ffffff`,
      glow: `rgba(108, 59, 255, 0.5)`,
      highlight: `rgba(255, 255, 255, 0.36)`,
      shadow: `0 32px 70px rgba(108, 59, 255, 0.5)`,
    },
    secondary: {
      text: `#0f172a`,
      hoverText: `#0f172a`,
      underline: `rgba(15, 23, 42, 0.65)`,
      background: `transparent`,
      hoverBackground: `rgba(255, 255, 255, 0.92)`,
      shadow: `0 16px 28px rgba(15, 23, 42, 0.12)`,
    },
  },
  cards: {
    surface: `#ffffff`,
    border: `rgba(148, 163, 184, 0.18)`,
    darkSurface: `rgba(15, 23, 42, 0.6)`,
    darkBorder: `rgba(148, 163, 184, 0.18)`,
  },
};

const mergeSection = <T extends Record<string, unknown>>(base: T, overrides?: DeepPartial<T>): T => {
  if (!overrides) {
    return base;
  }

  return {...base, ...overrides} as T;
};

export type CreateMicrositeThemeInput = DeepPartial<Omit<MicrositeTheme, `id` | `name`>> & {
  id: string;
  name: string;
};

export const createMicrositeTheme = ({id, name, ...overrides}: CreateMicrositeThemeInput): MicrositeTheme => {
  return {
    ...baseMicrositeTheme,
    ...overrides,
    id,
    name,
    palette: {...baseMicrositeTheme.palette, ...overrides.palette},
    text: {...baseMicrositeTheme.text, ...overrides.text},
    backgrounds: {...baseMicrositeTheme.backgrounds, ...overrides.backgrounds},
    gradients: {...baseMicrositeTheme.gradients, ...overrides.gradients},
    sections: {
      light: mergeSection(baseMicrositeTheme.sections.light, overrides.sections?.light),
      dark: mergeSection(baseMicrositeTheme.sections.dark, overrides.sections?.dark),
    },
    ctas: {
      primary: mergeSection(baseMicrositeTheme.ctas.primary, overrides.ctas?.primary),
      secondary: mergeSection(baseMicrositeTheme.ctas.secondary, overrides.ctas?.secondary),
    },
    cards: mergeSection(baseMicrositeTheme.cards, overrides.cards),
  };
};

export const getDefaultMicrositeTheme = () => baseMicrositeTheme;
